import { Fragment, useContext, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import CommonButton from "../../common/CommonButton";
import ModalCloseButton from "../../common/ModalCloseButton";
import ReactSelector from "../../common/CommonReactSelector";
import { components } from "react-select";
import translations from "../../common/Translations";
import AppContext from "../../../context/AppContext";
import { getCombinedActivityData, getCombinedMapData, updateUserSubActivities, updateUserWorkflows } from "../../../service/Configuration";
import { toast } from "../../../utils/toast";

const Placeholder = (props) => <components.Placeholder {...props} />;

export default function ActivityMapFilter({ openModal, setOpenModal, userId }) {
    const { langMode, projectId } = useContext(AppContext);

    const [activity, setActivity] = useState([]);
    const [selectedActivity, setSelectedActivity] = useState([]);
    const [mapOptions, setMapOptions] = useState([]);
    const [selectedMap, setSelectedMap] = useState([]);

    const handleClose = () => setOpenModal(false);

    useEffect(() => {
        const fetchUserDefaultData = async () => {
            if (!projectId || !userId) return;

            try {
                const { allOptions, preselectedact } = await getCombinedActivityData(projectId, userId);

                setActivity(allOptions);
                setSelectedActivity(preselectedact);

                const preselectedIds = preselectedact.map((item) => item.value);


                const { allMaps, preselected } = await getCombinedMapData(preselectedIds, userId, allOptions);

                setMapOptions(allMaps);     // all available map options
                setSelectedMap(preselected); // only user-specific maps preselected

            } catch (error) {
                console.error("? Error fetching user default data:", error);
            }
        };

        if (openModal) fetchUserDefaultData();
    }, [openModal, projectId, userId]);

    const handleChangeActivity = async (selectedValue) => {
        setSelectedActivity(selectedValue);
        const selectedIds = selectedValue.map((item) => item.value);

        if (selectedValue.length > 0) {
            localStorage.setItem("workflowName2", selectedValue[0].label);
        }

        const { allMaps } = await getCombinedMapData(selectedIds, userId, activity);

        const mergedOptions = [
            ...allMaps,
            ...mapOptions,
        ].filter(
            (item, index, self) =>
                index === self.findIndex((t) => t.value === item.value)
        );

        setMapOptions(mergedOptions);
    };

    /** ?? Step 3: Map selection */
    const handleMapChange = (selectedValue) => {
        console.log('selectedValue', selectedValue)
        setSelectedMap(selectedValue);
    };


    const handleOkClick = async () => {
        try {
            if (!userId || selectedActivity.length === 0) {
                toast.error("Please select at least one activity.");
                return;
            }
            const workflowList = selectedActivity.map((item) => item.value);
            await updateUserWorkflows(userId, workflowList);

            const subActivities = {};
            selectedActivity.forEach((activity) => {
                const workflowId = activity.value;
                const relatedMaps = selectedMap
                    .filter((map) => map.activityId === workflowId)
                    .map((map) => map.value);

                if (relatedMaps.length > 0) {
                    subActivities[workflowId] = relatedMaps;
                }

            })
            if (Object.keys(subActivities).length > 0) {
                await updateUserSubActivities(userId, subActivities);
            } else {
                await updateUserSubActivities(userId, {})
            }
            toast.success("User activities and maps updated successfully!");
            handleClose();
        } catch (error) {
            toast.error(error?.response?.data?.body?.value || "Failed to update user activity/map. Please try again.");
            console.error("? Update failed:", error);
        }
    };
    return (
        <Transition appear show={openModal} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-50" />
                </Transition.Child>

                <div className="fixed inset-0 z-0 flex items-center justify-center p-3 sm:p-4 overflow-visible">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel
                            className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl transform transition-all flex flex-col overflow-hidden"
                            style={{ maxHeight: "calc(100dvh - 1rem)" }}
                        >
                            {/* Scroll lives on this inner wrapper, not the rounded Dialog.Panel
                                itself -- same fix as CompletionRuleModal.js/AssignmentRuleModal.js
                                so the scrollbar never squares off the rounded corner. */}
                            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
                                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                                    <Dialog.Title className="text-base font-semibold text-gray-800 truncate">
                                        {translations[langMode].selectActivityAndMap}
                                    </Dialog.Title>
                                    <ModalCloseButton onClick={handleClose} />
                                </div>

                                <div className="px-4 sm:px-6 py-5 flex flex-col gap-5">
                                    <div className="flex flex-col gap-3">
                                        <label className="text-sm font-semibold text-left text-gray-800 dark:text-white">
                                            {translations[langMode].selectActivityLabel}
                                        </label>
                                        <ReactSelector
                                            options={activity}
                                            onChange={handleChangeActivity}
                                            components={Placeholder}
                                            placeholder={translations[langMode].selectActivityLabel}
                                            isClearable={false}
                                            value={selectedActivity}
                                            isMulti
                                            menuPortalTarget={document.body}
                                            selectedValuesMaxHeight="160px"
                                            maxMenuHeight={260}
                                            autoScrollMenuIntoView
                                        />
                                    </div>
                                    <div className="flex flex-col gap-3">
                                        <label className="text-sm font-semibold text-left text-gray-800 dark:text-white">
                                            {translations[langMode].selectMapLabel}
                                        </label>
                                        <ReactSelector
                                            options={mapOptions}
                                            onChange={handleMapChange}
                                            components={Placeholder}
                                            placeholder={translations[langMode].selectMapLabel}
                                            isClearable={false}
                                            value={selectedMap}
                                            isMulti
                                            menuPortalTarget={document.body}
                                            selectedValuesMaxHeight="160px"
                                            maxMenuHeight={260}
                                            autoScrollMenuIntoView
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
                                    <CommonButton colorIntent="danger" variant="danger" onClick={handleClose}>
                                        {translations[langMode]?.cancel}
                                    </CommonButton>
                                    <CommonButton colorIntent="primary"
                                        onClick={handleOkClick}
                                        variant="primary"
                                    >
                                        {translations[langMode]?.ok}
                                    </CommonButton>
                                </div>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
}
